import type { AnyValue } from "@/lib/rowTypes";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { Button } from "@/components/ui/button";
import CancellationModal from "@/components/subscription/CancellationModal";
import {
  Users,
  CreditCard,
  AlertTriangle,
  ChevronDown,
  UserPlus,
  Trash2,
  LifeBuoy,
  MessageSquareMore,
} from "lucide-react";
import ParentIntelligenceHub from "@/components/parent/hub/ParentIntelligenceHub";
import AddChildForm from "@/components/parent/AddChildForm";
import PendingInvites from "@/components/parent/PendingInvites";
import ReferralShareCard from "@/components/parent/ReferralShareCard";
import HouseholdSetup from "@/components/parent/HouseholdSetup";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import PageShell from "@/components/premium/PageShell";
import { trackMetaEvent } from "@/lib/metaPixel";
import { trackParent } from "@/lib/learning/telemetry";
import PrivacyTrustCard from "@/components/parent/hub/intelligence/PrivacyTrustCard";
import ParentGoalsPicker from "@/components/parent/hub/intelligence/ParentGoalsPicker";
import CompetencySignalsCard from "@/components/parent/hub/intelligence/CompetencySignalsCard";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import UnassignedSeatCard from "@/components/learners/UnassignedSeatCard";
import { useLearnerSeats } from "@/hooks/useLearnerSeats";
import { useLearnerSeatCutover } from "@/hooks/useLearnerSeatCutover";
import {
  LayoutGrid,
  Users as TabUsers,
  Receipt as TabReceipt,
  ShieldCheck as TabShield,
} from "lucide-react";
import {
  ShieldCheck,
  Sparkles as SparklesIcon,
  Lock,
  Eye,
  KeyRound,
  FileText,
} from "lucide-react";
import {
  ParentGlassCard,
  ParentStatusChip,
  ParentTabBar,
  ParentDestructiveAction,
} from "@/components/parent/shared/parentPrimitives";

const ParentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    household,
    members,
    subscription,
    isParent,
    isSubscribed,
    startCheckout,
    createHousehold,
    removeMember,
    openBillingPortal,
    reactivateSubscription,
    loading: householdLoading,
  } = useHousehold();

  const [showCancellation, setShowCancellation] = useState(false);
  const [showAddChild, setShowAddChild] = useState(false);
  const { data: seatData, refetch: refetchSeats } = useLearnerSeats(household?.id);
  const { enabled: seatCutover } = useLearnerSeatCutover();
  const [inviteRefreshKey, setInviteRefreshKey] = useState(0);
  useEffect(() => { if (household?.id) void refetchSeats(); }, [household?.id, inviteRefreshKey, members.length, refetchSeats]);
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const [billingAction, setBillingAction] = useState<"checkout" | "portal" | "reactivate" | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [childActivity, setChildActivity] = useState<Record<string, { lessons: number; last: string | null }>>({});
  const [activeTab, setActiveTab] = useState<string>("command");
  const [ownedChildProfileCount, setOwnedChildProfileCount] = useState(0);

  const markParentOnboardingComplete = async () => {
    if (!user?.id) return;
    const { error } = await supabase
      .from("profiles")
      .upsert(
        {
          id: user.id,
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
        },
        { onConflict: "id" },
      );
    if (error) {
      console.warn("[parent-onboarding] completion update failed", error);
      return;
    }
    localStorage.setItem(`heirwise_onboarding_${user.id}`, "true");
  };

  // Fire parent_dashboard_viewed once per mount, and first_parent_dashboard_viewed
  // once ever per user (localStorage flag). Non-blocking.
  useEffect(() => {
    if (!user?.id) return;
    const firstKey = `hw_first_parent_dash_${user.id}`;
    const isFirst = !localStorage.getItem(firstKey);
    void trackMetaEvent("parent_dashboard_viewed", {
      externalId: user.id,
      customData: {
        household_id: household?.id ?? null,
        plan_status: subscription.status,
        is_first_view: isFirst,
        surface: "parent_dashboard",
      },
    });
    void trackParent({
      eventType: "parent_dashboard_viewed",
      householdId: household?.id ?? null,
      metadata: { is_first_view: isFirst },
    });
    if (isFirst) {
      localStorage.setItem(firstKey, "1");
      void trackMetaEvent("first_parent_dashboard_viewed", {
        externalId: user.id,
        customData: { household_id: household?.id ?? null },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!household || !isParent) {
      setPendingInviteCount(0);
      return;
    }

    const loadPendingInvites = async () => {
      const { data, error } = await supabase
        .from("household_invites")
        .select("id, status")
        .eq("household_id", household.id)
        .eq("status", "pending");

      if (error) {
        console.error("Failed to load pending invites:", error);
        return;
      }

      setPendingInviteCount((data || []).length);
    };

    void loadPendingInvites();
  }, [household, isParent, inviteRefreshKey]);

  // Fetch lightweight activity per child so we can sort + default-select the most-active one
  useEffect(() => {
    if (!household || !isParent) { setChildActivity({}); return; }
    const childIds = members.filter(m => m.role === "child").map(m => m.user_id);
    if (childIds.length === 0) { setChildActivity({}); return; }

    (async () => {
      const map: Record<string, { lessons: number; last: string | null }> = {};
      for (const id of childIds) map[id] = { lessons: 0, last: null };

      // Lesson count (no timestamp on user_progress)
      const { data: progress, error: pErr } = await supabase
        .from("user_progress")
        .select("user_id, completed")
        .in("user_id", childIds)
        .eq("completed", true);
      if (pErr) { console.error("Failed to load child progress:", pErr); }
      for (const row of (progress) || []) {
        const m = map[row.user_id] ?? { lessons: 0, last: null };
        m.lessons += 1;
        map[row.user_id] = m;
      }

      // Last activity from quiz_attempts
      const { data: attempts, error: aErr } = await supabase
        .from("quiz_attempts")
        .select("user_id, created_at")
        .in("user_id", childIds)
        .order("created_at", { ascending: false });
      if (aErr) { console.error("Failed to load child activity:", aErr); }
      for (const row of (attempts) || []) {
        const m = map[row.user_id] ?? { lessons: 0, last: null };
        if (!m.last || (row.created_at && row.created_at > m.last)) m.last = row.created_at;
        map[row.user_id] = m;
      }

      setChildActivity(map);
    })();
  }, [household, isParent, members]);

  useEffect(() => {
    if (!user?.id || !isParent) {
      setOwnedChildProfileCount(0);
      return;
    }
    let active = true;
    void supabase
      .from("child_profiles")
      .select("id", { count: "exact", head: true })
      .eq("parent_user_id", user.id)
      .then(({ count, error }) => {
        if (!active) return;
        if (error) {
          console.warn("[parent-onboarding] child profile count failed", error);
          return;
        }
        setOwnedChildProfileCount(count ?? 0);
      });
    return () => { active = false; };
  }, [user?.id, isParent]);

  useEffect(() => {
    if (!user?.id || householdLoading || !isParent) return;
    const hasChildMember = members.some((member) => member.role === "child");
    if (!hasChildMember && ownedChildProfileCount === 0) return;
    void markParentOnboardingComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, householdLoading, isParent, members, ownedChildProfileCount]);

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMemberId(memberId);
    try {
      await removeMember(memberId);
      toast.success("Family member removed.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to remove member.");
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleCheckout = async () => {
    setBillingAction("checkout");
    try {
      await startCheckout();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to start checkout.");
    } finally {
      setBillingAction(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    setBillingAction("portal");
    try {
      await openBillingPortal();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to open billing settings.");
    } finally {
      setBillingAction(null);
    }
  };

  const handleReactivate = async () => {
    setBillingAction("reactivate");
    try {
      await reactivateSubscription();
      toast.success("Subscription reactivated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to reactivate subscription.");
    } finally {
      setBillingAction(null);
    }
  };

  if (householdLoading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </PageShell>
    );
  }

  if (household && !isParent) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center p-4">
          <div className="max-w-sm text-center space-y-4">
            <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto" />
            <h2 className="font-heading text-xl font-semibold text-foreground">Parent Access Only</h2>
            <p className="text-sm text-muted-foreground">This hub is only available to the household owner. Ask your parent to manage billing and family settings.</p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!household) {
    return (
      <PageShell>
        <div className="pl-12 lg:pl-0">
          <h1 className="font-heading text-xl font-semibold text-foreground">Parent Hub</h1>
          <p className="mt-1 text-sm text-muted-foreground">Set up your household to start tracking your child's progress.</p>
        </div>
          <HouseholdSetup
            onCreateHousehold={async (name) => {
              const created = await createHousehold(name);
              setShowAddChild(true);
              return created;
            }}
          />
      </PageShell>
    );
  }

  const childMembers = members.filter((member) => member.role === "child");
  const childOptions = childMembers
    .map((m) => ({
      user_id: m.user_id,
      display_name: m.display_name || "Child",
      lessons: childActivity[m.user_id]?.lessons ?? 0,
      last_activity: childActivity[m.user_id]?.last ?? null,
    }))
    .sort((a, b) => {
      // Most-active child first so the default-selected card shows real data
      if (b.lessons !== a.lessons) return b.lessons - a.lessons;
      const at = a.last_activity ? Date.parse(a.last_activity) : 0;
      const bt = b.last_activity ? Date.parse(b.last_activity) : 0;
      return bt - at;
    });
  // Canonical capacity: max_learners via learner_seat_capacity (counts seats, pending invites, legacy children).
  const seatsLoading = !seatData;
  const maxChildProfiles = seatData?.summary.max ?? 0;
  const reservedChildSlots = seatData?.summary.used ?? childMembers.length + pendingInviteCount;
  const remainingChildSlots = seatData?.summary.available ?? 0;
  // While seats load, don't flash "0 of 0" or disable invites; the server still enforces capacity.
  const childCapacityReached = !seatsLoading && remainingChildSlots === 0;
  const statusLabel = isSubscribed
    ? subscription.cancel_at_period_end
      ? "Cancels at period end"
      : "Active"
    : subscription.status === "canceled"
      ? "Canceled"
      : "Inactive";
  const paymentMethodLabel = subscription.payment_method?.last4
    ? `${subscription.payment_method.brand?.toUpperCase() || "Card"} ending in ${subscription.payment_method.last4}`
    : "No payment method on file";
  const canManageBilling = subscription.status !== "inactive" || subscription.invoices.length > 0 || Boolean(subscription.payment_method);

  return (
    <PageShell wide>
      <div data-testid="parent-hub-command-center" className="space-y-3 sm:space-y-4">
        {seatCutover && (seatData?.summary.unassigned ?? 0) > 0 && (
          <UnassignedSeatCard
            unassigned={seatData!.summary.unassigned}
            max={seatData!.summary.max}
            onAssign={() => setShowAddChild(true)}
          />
        )}

        {!seatCutover && childOptions.length === 0 && (
          <div className="glass-card space-y-4 p-6 sm:p-8">
            <div className="text-center space-y-2">
              <Users className="mx-auto h-10 w-10 text-muted-foreground" />
              <h3 className="font-heading text-lg font-bold text-foreground">Add your first child to get started</h3>
              <p className="text-sm text-muted-foreground">
                Invite your teen by email, or create a child profile so you can watch their progress.
              </p>
            </div>
            <AddChildForm
              householdId={household.id}
              onInviteSent={() => setInviteRefreshKey((key) => key + 1)}
              disabled={childCapacityReached}
              slotsRemaining={remainingChildSlots}
            />
            <PendingInvites householdId={household.id} refreshKey={inviteRefreshKey} />
          </div>
        )}

        {childOptions.length > 0 && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3 sm:space-y-4">
          <ParentTabBar
            active={activeTab}
            onSelect={setActiveTab}
            items={[
              { value: "command", label: "Command", icon: LayoutGrid },
              { value: "family", label: "Family", icon: TabUsers },
              { value: "billing", label: "Billing", icon: TabReceipt },
              { value: "privacy", label: "Privacy", icon: TabShield },
            ]}
          />

          <TabsContent value="command" className="mt-0 focus-visible:outline-none">
            <ParentIntelligenceHub
              children={childOptions.map((c) => ({ user_id: c.user_id, display_name: c.display_name }))}
            />
          </TabsContent>

          <TabsContent value="family" className="mt-0 focus-visible:outline-none">
            <ParentGlassCard className="p-5 sm:p-6">
              <div className="space-y-5">
          {/* Header: household identity + plan badge */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Family</p>
              <h2 className="font-heading text-xl font-bold text-foreground truncate">{household.name}</h2>
              <p className="text-sm text-muted-foreground">
                {childMembers.length} child profile{childMembers.length === 1 ? "" : "s"} · {seatsLoading ? "Checking learner seats…" : `${reservedChildSlots} of ${maxChildProfiles} slots used`}
                {pendingInviteCount > 0 ? ` · ${pendingInviteCount} pending invite${pendingInviteCount === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          </div>

          {/* Members list with inline add */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Family Members ({members.length})</span>
              </div>
              <button
                onClick={() => !childCapacityReached && setShowAddChild(!showAddChild)}
                disabled={childCapacityReached}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card/50 px-3 py-1 text-xs font-medium text-foreground transition hover:bg-card",
                  childCapacityReached && "opacity-50 cursor-not-allowed"
                )}
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add member
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showAddChild && "rotate-180")} />
              </button>
            </div>

            <div className="space-y-2">
              {members.map((member) => (
                <div key={member.id} className="relative overflow-hidden flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-gradient-to-br from-card/70 via-card/55 to-card/40 px-3.5 py-2.5 backdrop-blur-xl">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 via-primary/10 to-secondary/20 text-[10px] font-semibold text-foreground ring-1 ring-primary/20">
                      {(member.display_name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{member.display_name || "Member"}</p>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <ParentStatusChip
                          tone={member.role === "child" ? "brand" : "ready"}
                          label={member.role === "child" ? "Teen" : "Parent"}
                        />
                        {member.role !== "child" && (
                          <span className="text-[10px] text-muted-foreground truncate">{user?.email || "you"}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {member.role === "child" && (
                    <ParentDestructiveAction
                      triggerLabel={removingMemberId === member.id ? "Removing…" : "Remove"}
                      title={`Remove ${member.display_name || "this child"}?`}
                      description="They will lose access to HeirWise on your household. You can re-invite them later."
                      onConfirm={() => handleRemoveMember(member.id)}
                      disabled={removingMemberId === member.id}
                    />
                  )}
                </div>
              ))}
            </div>

            {childCapacityReached ? (
              showAddChild && (
                <div className="rounded-xl border border-border/60 bg-card/40 p-3 text-sm text-muted-foreground">
                  Your household has reached the 4-child limit. Remove a pending invite or child profile to open a slot.
                </div>
              )
            ) : showAddChild ? (
              <AddChildForm
                householdId={household.id}
                onInviteSent={() => {
                  setInviteRefreshKey((key) => key + 1);
                }}
                disabled={childCapacityReached}
                slotsRemaining={remainingChildSlots}
              />
            ) : null}

            <PendingInvites householdId={household.id} refreshKey={inviteRefreshKey} />
          </div>

          <ReferralShareCard
            referralCode={(household).referral_code ?? null}
            foundingMember={(household).founding_member ?? false}
          />

          <CompetencySignalsCard
            childId={childOptions[0]?.user_id ?? null}
            childName={(childOptions[0])?.display_name ?? (childOptions[0] as AnyValue)?.first_name ?? null}
          />

          <ParentGoalsPicker childId={childOptions[0]?.user_id ?? null} />

          {/* Help & feedback footer */}
          <div className="pt-3 border-t border-border/60 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button variant="outline" size="sm" className="sm:w-auto" onClick={() => navigate("/support?category=parent_dashboard_issue")}>
              <LifeBuoy className="mr-2 h-4 w-4" />
              Help
            </Button>
            <Button variant="outline" size="sm" className="sm:w-auto" onClick={() => navigate("/feedback?type=feature_request")}>
              <MessageSquareMore className="mr-2 h-4 w-4" />
              Feedback
            </Button>
          </div>
              </div>
            </ParentGlassCard>
          </TabsContent>

          <TabsContent value="billing" className="mt-0 focus-visible:outline-none">
            {seatCutover && (
              <a href="/settings/billing" className="mb-3 inline-flex text-sm font-medium text-primary underline-offset-4 hover:underline">
                Open Billing &amp; Subscription settings
              </a>
            )}
            <ParentGlassCard className="p-5 sm:p-6">
              <div className="space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-2.5 min-w-0">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25">
                      <SparklesIcon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Billing</p>
                      <h2 className="font-heading text-lg font-semibold text-foreground sm:text-xl">Subscription</h2>
                      <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Your HeirWise plan powers every learner in your household.</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-1 sm:items-end">
                    <ParentStatusChip tone="ready" label="Plan active" />
                    <p className="text-[11px] text-muted-foreground">Billed through Stripe</p>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-3.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Plan</p>
                    <p className="mt-1 text-sm font-semibold text-foreground">HeirWise plan</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Full feature access</p>
                  </div>
                  <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-3.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Cost today</p>
                    <p className="mt-1 text-sm font-semibold text-foreground">$0</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">Manage billing from your account</p>
                  </div>
                  <div className="rounded-2xl border border-white/[0.06] bg-background/30 p-3.5">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Next action</p>
                    <p className="mt-1 text-sm font-semibold text-foreground">No action needed</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">We'll email you before billing returns</p>
                  </div>
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Your plan is active. Focus on your teen's progress — billing is handled automatically.
                </p>
              </div>
            </ParentGlassCard>
          </TabsContent>

          <TabsContent value="privacy" className="mt-0 focus-visible:outline-none">
            <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
              <div className="lg:col-span-2">
                <PrivacyTrustCard />
              </div>
              <TrustTile
                icon={ShieldCheck}
                title="What HeirWise uses data for"
                body="Lesson progress, quiz performance, and simulation choices personalize learning and surface parent insights. That's it."
              />
              <TrustTile
                icon={Lock}
                title="What HeirWise never does"
                body="We never sell child data, never share it with advertisers, and never use it to train third-party models."
              />
              <TrustTile
                icon={Eye}
                title="Parent visibility"
                body="You see insights, not surveillance. We summarize progress and confidence — not every keystroke."
              />
              <TrustTile
                icon={KeyRound}
                title="Account security"
                body="Manage password, sessions, and trusted devices from Account Security."
                onClick={() => navigate("/settings/security")}
                cta="Open security"
              />
              <TrustTile
                icon={FileText}
                title="Children's privacy notice"
                body="Read how we protect learners under 18 in plain language."
                onClick={() => navigate("/children-privacy-notice")}
                cta="Read notice"
              />
              <TrustTile
                icon={Trash2}
                title="Data deletion"
                body="Request deletion of your household's HeirWise data at any time."
                onClick={() => navigate("/data-deletion-request")}
                cta="Request deletion"
              />
            </div>
          </TabsContent>
        </Tabs>
        )}

      {showCancellation && <CancellationModal onClose={() => setShowCancellation(false)} />}
      </div>
    </PageShell>
  );
};

export default ParentDashboard;

function TrustTile({
  icon: Icon,
  title,
  body,
  onClick,
  cta,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  onClick?: () => void;
  cta?: string;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-4 backdrop-blur-xl shadow-[0_16px_40px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]">
      <div className="pointer-events-none absolute -top-12 -right-12 h-28 w-28 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{body}</p>
          {cta && onClick && (
            <button
              type="button"
              onClick={onClick}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-background/40 px-3 py-1 text-[11px] font-medium text-foreground transition hover:bg-background/60"
            >
              {cta}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

